import React, { useState } from "react";
import { Tabs, message } from "antd";
import {
  BillingHeader,
  ServiceTable,
  StorageTable,
  InvoicePreviewModal,
} from "./components";
import {
  ServiceForInvoice,
  StorageCalculation,
  Booking,
  InvoiceData,
  InvoiceLineItem,
} from "../types";

const { TabPane } = Tabs;

interface BillingComponentProps {
  booking: Booking | undefined;
  services: ServiceForInvoice[];
  storageDates: StorageCalculation[];
  averageStorage: number;
  totalInventoryValue: number;
  setServices: React.Dispatch<React.SetStateAction<ServiceForInvoice[]>>;
}

export const BillingBookingComponent: React.FC<BillingComponentProps> = ({
  booking,
  services,
  storageDates,
  averageStorage,
  totalInventoryValue,
  setServices,
}) => {
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [storageRate] = useState<number>(100);
  const [insuranceRate] = useState<number>(0.01);
  const [includeInsurance, setIncludeInsurance] = useState<boolean>(false);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState<boolean>(false);
  const [invoicePreview, setInvoicePreview] = useState<InvoiceData | null>(
    null
  );
  const [isPreviewModalVisible, setIsPreviewModalVisible] =
    useState<boolean>(false);

  const getTotalAmount = (): string => {
    let total = 0;

    services.forEach((service) => {
      if (selectedServices.includes(service.id)) {
        if (
          ["COLLECTION", "DELIVERY", "ON_SITE_SERVICE"].includes(
            service.serviceType
          )
        ) {
          const serviceHours = Math.max(
            service.totalHours,
            service.minimumHours
          );
          const vesselCost = service.vesselRate * serviceHours;
          const staffCost = service.staffRates.reduce(
            (acc, rate) => acc + rate * serviceHours,
            0
          );
          const supplyCost = service.suppliesUsed
            .filter((supply) => supply.selected)
            .reduce((acc, supply) => acc + supply.price * supply.quantity, 0);
          const adminCost = service.adminFees
            .filter((fee) => fee.selected)
            .reduce((acc, fee) => acc + fee.amount, 0);
          total += vesselCost + staffCost + supplyCost + adminCost;
        } else if (["WAREHOUSE_SERVICE"].includes(service.serviceType)) {
          // Use the same calculation as in formatServiceForTable
          const billableCubicFeet = service.inventoryCubicFootage;

          // For warehouse services, use the calculated amount based on cubic footage
          // This calculation should match the backend logic
          // $1 per cubic foot or $75, whichever is greater
          total += Math.max(billableCubicFeet, 75);

          // Add supply costs if any
          const supplyCost = service.suppliesUsed
            .filter((supply) => supply.selected)
            .reduce((acc, supply) => acc + supply.price * supply.quantity, 0);
          total += supplyCost;

          // Add admin fees if any
          const adminCost = service.adminFees
            .filter((fee) => fee.selected)
            .reduce((acc, fee) => acc + fee.amount, 0);
          total += adminCost;
        }
      }
    });

    if (selectedServices.includes("storage")) {
      total += averageStorage * storageRate;
      if (includeInsurance) {
        total += totalInventoryValue * insuranceRate;
      }
    }

    return total.toFixed(2);
  };

  const formatServiceForTable = (
    service: ServiceForInvoice
  ): { description: string; amount: number } => {
    let description = "";
    let amount = 0;

    if (
      ["COLLECTION", "DELIVERY", "ON_SITE_SERVICE"].includes(
        service.serviceType
      )
    ) {
      const serviceHours = Math.max(service.totalHours, service.minimumHours);
      const vesselCost = service.vesselRate * serviceHours;
      const staffCost = service.staffRates.reduce(
        (acc, rate) => acc + rate * serviceHours,
        0
      );
      description = `${service.serviceType} - ${service.serviceReference} on ${service.serviceDate}`;
      amount = vesselCost + staffCost;
    } else if (["WAREHOUSE_SERVICE"].includes(service.serviceType)) {
      // Use the values provided by the backend
      const billableCubicFeet = service.inventoryCubicFootage;

      // For warehouse services, use the calculated amount based on cubic footage
      // This calculation should match the backend logic
      // $1 per cubic foot or $75, whichever is greater
      amount = Math.max(billableCubicFeet, 75);

      description = `${service.serviceType} - ${billableCubicFeet.toFixed(
        2
      )} cubic feet`;
    }

    return { description, amount };
  };

  const toggleServiceSelection = (serviceId: string): void => {
    setSelectedServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const toggleStorageSelection = (): void => {
    setSelectedServices((prev) =>
      prev.includes("storage")
        ? prev.filter((id) => id !== "storage")
        : [...prev, "storage"]
    );
  };

  const toggleSupplySelection = (serviceId: string, supplyId: string): void => {
    setServices((prev) =>
      prev.map((service) =>
        service.id === serviceId
          ? {
              ...service,
              suppliesUsed: service.suppliesUsed.map((supply) =>
                supply.id === supplyId
                  ? { ...supply, selected: !supply.selected }
                  : supply
              ),
            }
          : service
      )
    );
  };

  const toggleAdminFeeSelection = (serviceId: string, feeId: string): void => {
    setServices((prev) =>
      prev.map((service) =>
        service.id === serviceId
          ? {
              ...service,
              adminFees: service.adminFees.map((fee) =>
                fee.id === feeId ? { ...fee, selected: !fee.selected } : fee
              ),
            }
          : service
      )
    );
  };

  const handleCreateInvoice = (): void => {
    if (selectedServices.length === 0) {
      message.error("Please select at least one service to invoice");
      return;
    }

    try {
      setIsCreatingInvoice(true);
      const lineItems: InvoiceLineItem[] = [];

      services.forEach((service) => {
        if (selectedServices.includes(service.id)) {
          const { description, amount } = formatServiceForTable(service);
          lineItems.push({
            Description: description,
            Amount: amount,
            DetailType: "SalesItemLineDetail",
            SalesItemLineDetail: {
              ItemRef: {
                value:
                  service.serviceType === "COLLECTION" ||
                  service.serviceType === "DELIVERY"
                    ? "1"
                    : "2",
              },
            },
          });

          service.suppliesUsed
            .filter((supply) => supply.selected)
            .forEach((supply) => {
              lineItems.push({
                Description: `${supply.name} x ${supply.quantity}`,
                Amount: supply.price * supply.quantity,
                DetailType: "SalesItemLineDetail",
                SalesItemLineDetail: {
                  ItemRef: { value: "3" },
                },
              });
            });

          service.adminFees
            .filter((fee) => fee.selected)
            .forEach((fee) => {
              lineItems.push({
                Description: fee.name,
                Amount: fee.amount,
                DetailType: "SalesItemLineDetail",
                SalesItemLineDetail: {
                  ItemRef: { value: "4" },
                },
              });
            });
        }
      });

      if (selectedServices.includes("storage")) {
        lineItems.push({
          Description: `Monthly Storage - ${averageStorage.toFixed(
            2
          )} cubic feet @ $${storageRate}/cubic foot`,
          Amount: averageStorage * storageRate,
          DetailType: "SalesItemLineDetail",
          SalesItemLineDetail: {
            ItemRef: { value: "5" },
          },
        });

        if (includeInsurance) {
          const insuranceAmount = totalInventoryValue * insuranceRate;
          lineItems.push({
            Description: `Insurance (${(insuranceRate * 100).toFixed(
              2
            )}% of $${totalInventoryValue.toFixed(2)})`,
            Amount: insuranceAmount,
            DetailType: "SalesItemLineDetail",
            SalesItemLineDetail: {
              ItemRef: { value: "6" },
            },
          });
        }
      }

      const invoiceData: InvoiceData = {
        Line: lineItems,
        CustomerRef: {
          value: booking?.client?.quickBooksId ?? "",
        },
        CustomField: [
          {
            DefinitionId: "1",
            Name: "Booking Reference",
            Type: "StringType",
            StringValue: booking?.bookingReference ?? "",
          },
        ],
      };

      setInvoicePreview(invoiceData);
      setIsPreviewModalVisible(true);
      setIsCreatingInvoice(false);
    } catch (error) {
      console.error("Error creating invoice preview:", error);
      message.error("Failed to create invoice preview");
      setIsCreatingInvoice(false);
    }
  };

  const handleConfirmInvoice = (): void => {
    try {
      setIsCreatingInvoice(true);
      setIsPreviewModalVisible(false);
      if (!invoicePreview) throw new Error("No invoice preview available");
      // Mock invoice creation, since API is not used
      const invoiceId = "INV-12345"; // Mock ID
      const updatedServices = services.map((service) =>
        selectedServices.includes(service.id)
          ? { ...service, invoiceStatus: "invoiced" as const, invoiceId }
          : service
      );

      setServices(updatedServices);
      setSelectedServices([]);
      setIsCreatingInvoice(false);
      message.success(`Invoice ${invoiceId} created successfully`);
    } catch (error) {
      console.error("Error creating invoice:", error);
      message.error("Failed to create invoice");
      setIsCreatingInvoice(false);
    }
  };

  return (
    <div className="p-6">
      <BillingHeader
        booking={booking}
        totalAmount={getTotalAmount()}
        onCreateInvoice={handleCreateInvoice}
        isCreatingInvoice={isCreatingInvoice}
        selectedServices={selectedServices}
      />
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Services to Invoice</h3>
            <div className="text-right">
              <p className="text-xl font-medium">Total: ${getTotalAmount()}</p>
            </div>
          </div>
        </div>
        <Tabs defaultActiveKey="services" className="p-4">
          <TabPane tab="Services" key="services">
            <ServiceTable
              services={services}
              selectedServices={selectedServices}
              toggleServiceSelection={toggleServiceSelection}
              toggleSupplySelection={toggleSupplySelection}
              toggleAdminFeeSelection={toggleAdminFeeSelection}
              formatServiceForTable={formatServiceForTable}
            />
          </TabPane>
          <TabPane tab="Storage" key="storage">
            <StorageTable
              selectedServices={selectedServices}
              toggleStorageSelection={toggleStorageSelection}
              includeInsurance={includeInsurance}
              setIncludeInsurance={setIncludeInsurance}
              storageDates={storageDates}
              averageStorage={averageStorage}
              storageRate={storageRate}
              totalInventoryValue={totalInventoryValue}
              insuranceRate={insuranceRate}
              booking={booking}
            />
          </TabPane>
        </Tabs>
      </div>
      <InvoicePreviewModal
        invoicePreview={invoicePreview}
        isPreviewModalVisible={isPreviewModalVisible}
        setIsPreviewModalVisible={setIsPreviewModalVisible}
        handleConfirmInvoice={handleConfirmInvoice}
        isCreatingInvoice={isCreatingInvoice}
        booking={booking}
      />
    </div>
  );
};

export default BillingBookingComponent;
